import { ChevronRight, Clock, FileText, FolderOpen, Loader2, Search, Share2, Users } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { StatusBadge } from "@lexiun/org-admin";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { usePlatformApi } from "../hooks/usePlatformApi";
import type { CaseRegistryEntry, Organization } from "../types/platform";

export function CaseRegistry() {
  const api = usePlatformApi();

  const [cases, setCases] = useState<CaseRegistryEntry[]>([]);
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [orgFilter, setOrgFilter] = useState("");
  const [selectedCase, setSelectedCase] = useState<CaseRegistryEntry | null>(null);
  const [caseDetail, setCaseDetail] = useState<Record<string, unknown> | null>(null);
  const [detailTab, setDetailTab] = useState<"overview" | "parties" | "documents" | "timeline">("overview");
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const orgsRes = await api.listOrgs({ limit: 100 });
      setOrgs(orgsRes.data);

      if (orgFilter) {
        // Filter by specific org
        const casesRes = await api.listCases(orgFilter);
        setCases(casesRes.data);
      } else {
        // Fetch cases from all active orgs (platform admin view)
        const activeOrgs = orgsRes.data.filter((o) => o.status === "ACTIVE");
        const allCases = await Promise.all(
          activeOrgs.map((o) => api.listCases(o.orgId).then((r) => r.data).catch(() => [] as CaseRegistryEntry[])),
        );
        // Deduplicate by caseId (shared cases appear in multiple orgs)
        const seen = new Set<string>();
        const merged: CaseRegistryEntry[] = [];
        for (const batch of allCases) {
          for (const c of batch) {
            if (!seen.has(c.caseId)) {
              seen.add(c.caseId);
              merged.push(c);
            }
          }
        }
        setCases(merged.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()));
      }
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load cases");
    } finally {
      setLoading(false);
    }
  }, [api, orgFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const selectCase = useCallback(async (c: CaseRegistryEntry) => {
    setSelectedCase(c);
    setDetailTab("overview");
    setCaseDetail(null);
    setDetailLoading(true);
    try {
      const res = await api.getCase(c.caseId);
      setCaseDetail(res.data as unknown as Record<string, unknown>);
    } catch {
      // Fall back to registry entry only
    } finally {
      setDetailLoading(false);
    }
  }, [api]);

  // Guard: a registry entry with a missing ownerOrgId (or an empty
  // sharedOrgIds element) must NOT crash the whole page via the
  // ErrorBoundary — `orgId.slice` on undefined was the `/cases`
  // "Cannot read properties of undefined (reading 'slice')" crash.
  const orgName = (orgId?: string | null) =>
    orgId
      ? (orgs.find((o) => o.orgId === orgId)?.name ?? orgId.slice(0, 12) + "...")
      : "—";

  // Unique statuses for filter
  const statuses = [...new Set(cases.map((c) => c.status))].sort();

  const filtered = cases.filter((c) => {
    if (statusFilter && c.status !== statusFilter) { return false; }
    if (search) {
      const q = search.toLowerCase();
      return c.title.toLowerCase().includes(q) || c.caseId.toLowerCase().includes(q);
    }
    return true;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Case Registry</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {cases.length} cases across {new Set(cases.map((c) => c.ownerOrgId)).size} organizations
          </p>
        </div>
        <FolderOpen className="h-6 w-6 text-slate-400" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative">
          <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-slate-400" />
          <input
            type="text"
            placeholder="Search cases..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="rounded-md border border-slate-200 bg-white py-1.5 pl-8 pr-3 text-sm outline-none focus:border-indigo-400 dark:border-slate-700 dark:bg-slate-900"
          />
        </div>
        <select
          value={orgFilter}
          onChange={(e) => setOrgFilter(e.target.value)}
          className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-900"
        >
          <option value="">All organizations</option>
          {orgs.map((o) => (
            <option key={o.orgId} value={o.orgId}>{o.name}</option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-900"
        >
          <option value="">All statuses</option>
          {statuses.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400">{error}</div>
      )}

      <div className="grid gap-4 lg:grid-cols-[1fr_350px]">
        {/* Case List */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Cases ({filtered.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center gap-2 py-8 text-sm text-slate-400">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading cases...
              </div>
            ) : filtered.length === 0 ? (
              <p className="py-8 text-center text-sm text-slate-400">No cases found</p>
            ) : (
              <div className="max-h-[500px] space-y-1 overflow-y-auto">
                {filtered.map((c) => (
                  <button
                    key={c.caseId}
                    type="button"
                    onClick={() => selectCase(c)}
                    className={`flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left text-sm transition-colors ${
                      selectedCase?.caseId === c.caseId
                        ? "bg-indigo-50 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300"
                        : "hover:bg-slate-50 dark:hover:bg-slate-800"
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium truncate">{c.title}</span>
                        <StatusBadge status={c.status.toUpperCase()} />
                        {c.sharedOrgIds.length > 0 && (
                          <span title="Shared case"><Share2 className="h-3 w-3 text-amber-500" /></span>
                        )}
                      </div>
                      <div className="mt-0.5 text-xs text-muted-foreground">
                        {orgName(c.ownerOrgId)} &middot; {new Date(c.updatedAt).toLocaleDateString()}
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 shrink-0 text-slate-300 dark:text-slate-600" />
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Case Detail (tabbed) */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {selectedCase ? selectedCase.title : "Select a case"}
            </CardTitle>
            {selectedCase && (
              <div className="mt-2 flex gap-0.5 border-b border-slate-200 dark:border-slate-800">
                {([
                  { id: "overview", label: "Overview", icon: FolderOpen },
                  { id: "parties", label: "Parties", icon: Users },
                  { id: "documents", label: "Documents", icon: FileText },
                  { id: "timeline", label: "Timeline", icon: Clock },
                ] as const).map(({ id, label, icon: Icon }) => (
                  <button key={id} type="button" onClick={() => setDetailTab(id)}
                    className={`inline-flex items-center gap-1 px-2 py-1.5 text-[10px] font-medium transition-colors ${
                      detailTab === id
                        ? "border-b-2 border-indigo-500 text-indigo-600 dark:text-indigo-400"
                        : "text-slate-500 hover:text-slate-700 dark:text-slate-400"
                    }`}>
                    <Icon className="h-3 w-3" />{label}
                  </button>
                ))}
              </div>
            )}
          </CardHeader>
          <CardContent>
            {!selectedCase ? (
              <p className="py-8 text-center text-sm text-slate-400">Click a case to view details</p>
            ) : detailLoading ? (
              <div className="flex items-center gap-2 py-8 text-sm text-slate-400">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading...
              </div>
            ) : detailTab === "overview" ? (
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2">
                  <StatusBadge status={selectedCase.status.toUpperCase()} />
                  {selectedCase.sharedOrgIds.length > 0 && (
                    <span className="flex items-center gap-1 text-[10px] text-amber-500"><Share2 className="h-3 w-3" /> Shared</span>
                  )}
                </div>
                {/* Description — the registry mirrors it best-effort from the
                    authoritative workspace write (case-ops backend: the
                    runtime's `case.update` T1 writes CASE_MODEL.json + mirrors
                    into the registry `description` field, T3). A loaded
                    `caseDetail` (fresh `getCase`) is authoritative — use its
                    value verbatim so an explicit "" clear renders nothing;
                    fall back to the list value only until the detail lands. */}
                {(() => {
                  const description =
                    caseDetail && typeof caseDetail.description === "string"
                      ? caseDetail.description
                      : (selectedCase.description ?? "");
                  return description ? (
                    <div>
                      <p className="text-xs text-muted-foreground">Description</p>
                      <p className="whitespace-pre-wrap text-sm">{description}</p>
                    </div>
                  ) : null;
                })()}
                <div>
                  <p className="text-xs text-muted-foreground">Case ID</p>
                  <p className="font-mono text-xs">{selectedCase.caseId}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Owner</p>
                  <p>{orgName(selectedCase.ownerOrgId)}</p>
                </div>
                {selectedCase.sharedOrgIds.length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground">Shared With</p>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {selectedCase.sharedOrgIds.map((id) => (
                        <Badge key={id} variant="outline" className="text-[10px]">{orgName(id)}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <p className="text-xs text-muted-foreground">Created By</p>
                  <p className="font-mono text-xs">{selectedCase.createdBy}</p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><p className="text-xs text-muted-foreground">Created</p><p className="text-xs">{new Date(selectedCase.createdAt).toLocaleString()}</p></div>
                  <div><p className="text-xs text-muted-foreground">Updated</p><p className="text-xs">{new Date(selectedCase.updatedAt).toLocaleString()}</p></div>
                </div>
                {caseDetail && (
                  <>
                    {caseDetail.documentCount != null && (
                      <div><p className="text-xs text-muted-foreground">Documents</p><p className="text-sm font-semibold">{String(caseDetail.documentCount)}</p></div>
                    )}
                    {caseDetail.processingStatus && (
                      <div><p className="text-xs text-muted-foreground">Processing</p><StatusBadge status={String(caseDetail.processingStatus).toUpperCase()} /></div>
                    )}
                  </>
                )}
              </div>
            ) : detailTab === "parties" ? (
              <div className="space-y-2 text-sm">
                {caseDetail?.parties && Array.isArray(caseDetail.parties) && caseDetail.parties.length > 0 ? (
                  (caseDetail.parties as Record<string, unknown>[]).map((p, i) => {
                    const name = String(p.name ?? p.displayName ?? `Party ${i + 1}`);
                    const role = p.role ? String(p.role) : null;
                    const doc = p.document ? String(p.document) : null;
                    return (
                      <div key={i} className="rounded border border-slate-100 p-2 dark:border-slate-800">
                        <p className="font-medium">{name}</p>
                        {role && <p className="text-xs text-muted-foreground">{role}</p>}
                        {doc && <p className="font-mono text-[10px] text-muted-foreground">{doc}</p>}
                      </div>
                    );
                  })
                ) : (
                  <p className="py-4 text-center text-slate-400">No parties recorded</p>
                )}
              </div>
            ) : detailTab === "documents" ? (
              <div className="space-y-2 text-sm">
                {caseDetail?.documentCount != null && Number(caseDetail.documentCount) > 0 ? (
                  <div>
                    <p className="text-muted-foreground">{String(caseDetail.documentCount)} documents in workspace</p>
                    <p className="mt-1 text-xs text-slate-400">Document listing requires runtime Admin API access. Use "Manage Instance" on the org user page to browse documents.</p>
                  </div>
                ) : (
                  <p className="py-4 text-center text-slate-400">No documents</p>
                )}
              </div>
            ) : detailTab === "timeline" ? (
              <div className="space-y-2 text-sm">
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2 text-xs">
                    <div className="h-2 w-2 rounded-full bg-green-500" />
                    <span>Created</span>
                    <span className="ml-auto text-muted-foreground">{new Date(selectedCase.createdAt).toLocaleString()}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <div className="h-2 w-2 rounded-full bg-blue-500" />
                    <span>Last Updated</span>
                    <span className="ml-auto text-muted-foreground">{new Date(selectedCase.updatedAt).toLocaleString()}</span>
                  </div>
                  {caseDetail?.metadata != null && typeof caseDetail.metadata === "object" && (
                    <div className="mt-3">
                      <p className="text-xs text-muted-foreground">Metadata</p>
                      <pre className="mt-1 max-h-40 overflow-auto rounded bg-slate-50 p-2 text-[10px] dark:bg-slate-800">
                        {JSON.stringify(caseDetail.metadata as object, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
